import {
  createCodeableConcept,
  createIdentifier,
  getAddressLines,
  getCity,
  getCountry,
  getEmail,
  getIdentifier,
  getPostalCode,
  getPrimaryPhoneNumber,
  getSecondaryPhoneNumber,
  IdentifierCode,
  OrganizationCode,
  OrganizationCodeDescription,
} from '@quromedical/fhir-common';
import { compact, first, last } from '@quromedical/utils';
import type { Address } from 'fhir/r4';

import {
  type Contact,
  createAddressExtensionList,
  getAddressPlaceId,
  getCoordinates,
} from '../person';
import type { WithId } from '../types';
import { createAddressList } from './address';
import {
  createEmailContactPoint,
  createPrimaryPhoneContactPoint,
  createSecondaryPhoneContactPoint,
  mergeContactPoints,
} from './contact-point';
import type {
  OrganizationContactUpdateRequest,
  OrganizationCreateResponse,
  OrganizationGeneral,
  OrganizationGeneralUpdateRequest,
  OrganizationUpdateRequest,
  PartialOrganization,
} from './schema';

export const typeCodes = Object.values(OrganizationCode);

export const getDescription = (code?: OrganizationCode): string | undefined => {
  if (!code) {
    return undefined;
  }

  return OrganizationCodeDescription[code];
};

type OrganizationAddress = Pick<
  Contact,
  | 'addressCity'
  | 'addressPostalCode'
  | 'addressLines'
  | 'addressCountry'
  | 'addressPlaceId'
  | 'addressCoordinates'
>;

const createAddress = (
  address: Partial<OrganizationAddress>,
): fhir4.Address[] | undefined => {
  const {
    addressCity: city,
    addressPostalCode: postalCode,
    addressLines,
    addressCountry: country,
  } = address;

  const line = addressLines ? addressLines : undefined;
  const extension = createAddressExtensionList(address);
  return createAddressList({
    line,
    postalCode,
    city,
    country,
    extension,
  });
};

const createType = (type?: OrganizationCode) => {
  if (!type) {
    return undefined;
  }
  const code = createCodeableConcept(type);
  return [code];
};

export const isGeneralInsurance = (
  general?: Partial<OrganizationGeneral>,
): general is Partial<OrganizationGeneral> =>
  general?.type === OrganizationCode.InsuranceCompany;

const createGeneralIdentifier = (
  general?: Partial<OrganizationGeneral>,
): fhir4.Identifier[] | undefined => {
  const isInsurance = isGeneralInsurance(general);
  const schemeCode = isInsurance && general.schemeCode;

  if (!schemeCode) {
    return undefined;
  }

  return [createIdentifier(IdentifierCode.organizationSchemeCode, schemeCode)];
};

export const getOrganizationSchemeCode = (
  organization?: Partial<OrganizationGeneral>,
): string | undefined => {
  const isInsurance = isGeneralInsurance(organization);

  if (!isInsurance) {
    return undefined;
  }

  return organization.schemeCode;
};

export const mapModelToFhirOrganization = (
  form: Partial<PartialOrganization>,
): fhir4.Organization => {
  const { general, contact } = form;
  const email = createEmailContactPoint(contact?.telecomEmail);
  const firstPhoneNumber = first(contact?.telecomPhoneNumbers);

  const multiplePhoneNumbers =
    contact?.telecomPhoneNumbers && contact?.telecomPhoneNumbers.length > 1;

  const alternativePhoneNumber = multiplePhoneNumbers
    ? last(contact?.telecomPhoneNumbers)
    : undefined;

  const primaryPhone = createPrimaryPhoneContactPoint(firstPhoneNumber);
  const secondaryPhone = createSecondaryPhoneContactPoint(
    alternativePhoneNumber,
  );
  const telecom = mergeContactPoints([email, primaryPhone, secondaryPhone]);

  const contactAddress: Partial<OrganizationAddress> = {
    addressCity: contact?.addressCity,
    addressCountry: contact?.addressCountry,
    addressLines: compact(contact?.addressLines),
    addressPostalCode: contact?.addressPostalCode,
    addressPlaceId: contact?.addressPlaceId,
    addressCoordinates: contact?.addressCoordinates,
  };
  const address = createAddress(contactAddress);
  const type = createType(general?.type);
  const name = general?.name;

  const identifier = createGeneralIdentifier(general);

  return {
    resourceType: 'Organization',
    identifier,
    name,
    type,
    address,
    telecom,
  };
};

const getValidCode = (
  codings?: fhir4.Coding[],
): OrganizationCode | undefined => {
  const foundCode = codings?.find(({ code }) => {
    if (!code) {
      return false;
    }

    return typeCodes.includes(code as OrganizationCode);
  });

  return foundCode?.code as OrganizationCode | undefined;
};

const getTypeCode = (org: fhir4.Organization): OrganizationCode | undefined => {
  const code = org?.type?.find((type) => getValidCode(type.coding));

  return getValidCode(code?.coding);
};

const mapFhirToGeneral = (
  fhir: fhir4.Organization,
): Partial<OrganizationGeneral> => {
  const type = getTypeCode(fhir);
  const { name, identifier } = fhir;

  if (type !== OrganizationCode.InsuranceCompany) {
    return {
      name,
      type,
    };
  }

  const schemeCode = getIdentifier(
    IdentifierCode.organizationSchemeCode,
    identifier,
  );
  return {
    name,
    type,
    schemeCode,
  };
};

export const mapFhirToModelOrganization = (
  fhir: fhir4.Organization,
): PartialOrganization => {
  const { telecom, address } = fhir;

  const telecomEmail = getEmail(telecom);
  const telecomPhone = getPrimaryPhoneNumber(telecom);
  const secondaryPhone = getSecondaryPhoneNumber(telecom);
  const addressCity = getCity(address);
  const addressPostalCode = getPostalCode(address);
  const addressLines = getAddressLines(address);
  const firstAddress = first<Address>(address);
  const addressCountry = getCountry(firstAddress);
  const addressPlaceId = getAddressPlaceId(firstAddress?.extension);
  const addressCoordinates = getCoordinates(firstAddress);

  return {
    general: mapFhirToGeneral(fhir),
    contact: {
      telecomEmail,
      telecomPhoneNumbers: compact([telecomPhone, secondaryPhone]),
      addressCity,
      addressCountry,
      addressLines: compact(addressLines),
      addressPostalCode,
      addressPlaceId,
      addressCoordinates,
    },
  };
};

export const mapFhirToModelOrganizationWithId = (
  fhir: WithId<fhir4.Organization>,
): OrganizationCreateResponse => {
  const organization = mapFhirToModelOrganization(fhir);

  return {
    ...organization,
    id: fhir.id,
  };
};

export const mapUpdateModelToFhirOrganization = (
  form: WithId<OrganizationUpdateRequest>,
): fhir4.Organization => {
  const organization = mapModelToFhirOrganization(form);

  return {
    ...organization,
    id: form.id,
  };
};

export const mapUpdateGeneralModelToFhirOrganization = (
  fhir: WithId<fhir4.Organization>,
  general: OrganizationGeneralUpdateRequest,
): WithId<fhir4.Organization> => {
  const modelOrganization = mapFhirToModelOrganizationWithId(fhir);

  const { contact } = modelOrganization;

  const fhirOrganization = mapModelToFhirOrganization({
    contact,
    general,
  });

  return { ...fhirOrganization, id: fhir.id };
};

export const mapUpdateContactModelToFhirOrganization = (
  fhir: WithId<fhir4.Organization>,
  contact: OrganizationContactUpdateRequest,
): WithId<fhir4.Organization> => {
  const modelOrganization = mapFhirToModelOrganizationWithId(fhir);

  const { general } = modelOrganization;

  const fhirOrganization = mapModelToFhirOrganization({
    contact: contact.contact,
    general,
  });

  return { ...fhirOrganization, id: fhir.id };
};
